import { Injectable, Inject } from '@angular/core';
import { OidcClientService } from '@upmc-healthplan-npm/oidc-client';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { SERVICE_CONSUMER_PROFILE_URL } from 'src/app/tokens';
import { take, tap } from 'rxjs/operators';
//import { GoogleAnalyticsService } from './google-analytics.service';
import KJUR from 'jsrsasign';
import { Profile } from 'oidc-client';

@Injectable({
  providedIn: 'root'
})
export class UserRulesService {
  private user: UserType;

  constructor(private auth: OidcClientService, private http: HttpClient,
    @Inject(SERVICE_CONSUMER_PROFILE_URL) private consumerProfileUrl: string,
    //private googleAnalytics: GoogleAnalyticsService
  ) {
    this.user = new UserType();
  }

  /**
   * Store the correct id found in the member's token
   * @param profile object of claims
   */
  setPrimaryIdFromProfile(profile: Profile) {
    if(profile) {
      const memberWithTpaClaim = profile.medical_member_id_with_tpa ? profile.medical_member_id_with_tpa : profile.dv_member_id_with_tpa;
      if(memberWithTpaClaim) {
        this.setPrimaryId(memberWithTpaClaim.slice(0, 11));
        this.setTPAClientIdentifier(memberWithTpaClaim.slice(11, memberWithTpaClaim.length - 4));
      }
    }
  }

  /**
   * Stores the given string to be used as the tpa partition id
   * @param tpaClientIdentifier partition id
   */
  setTPAClientIdentifier(tpaClientIdentifier: string) {
    this.user.tpaClientIdentifier = tpaClientIdentifier;
  }

  /**
   * Stores the given string to be used as the primary
   * @param id member id
   */
  setPrimaryId(id: string) {
    this.user.primaryMemberId = id;
  }

  /**
   * Store the member id found in the query string
   * @param id member id
   */
  setSecondaryId(id: string) {
    if (id) {
    id = id.slice(0, 11);
    }
    this.user.secondaryMemberId = id;
    if(id) {
      sessionStorage.setItem('querymember', id);
    }
  }

  /**
   * Return the member id stored in the token, but if the session is
   * for a dependent then this will return the dependent's member id.
  */
  getPrimaryId() {
    if(this.user.isDependent) {
      return this.user.secondaryMemberId;
    } else {
      return this.user.primaryMemberId;
    }
  }

  /**
   * Return the recipient id of the member when they are chc or medicaid,
   * the dependent id when the session is for a dependent; otherwise return
   * the member id stored in the token.
   */
  getSecondaryId() {
    if(this.user.isDependent || this.user.isChcOrMa) {
      // secondary id will always be the chc/ma, dependent's, or member's id
      return this.user.secondaryMemberId;
    } else {
      // this will always be the member's medical/dv id
      return this.user.primaryMemberId;
    }
  }

  getTPAClientIdentifier() {
    return this.user.tpaClientIdentifier;
  }

  setIdToken(token: string) {
    this.user.idToken = token;
    this.getConsumerProfile().pipe(take(1))
      .subscribe((response: { eligibilityType: string, planType: string, corpCode: string }) => {
        //this.googleAnalytics.pushMemberInformation(response.eligibilityType, response.corpCode, response.planType, this.auth.getUser().profile.acr.includes('memberservicesrep'));
      }
      );
  }

  updateRecipientStatus(token: string) {
    this.user.idToken = token;
    return this.getConsumerProfile().pipe(tap(response => {
      //this.googleAnalytics.pushMemberInformation(response.eligibilityType, response.corpCode, response.planType, this.auth.getUser().profile.acr.includes('memberservicesrep'));
      const eligibilityType = response.eligibilityType.toLocaleLowerCase();
      this.user.isChcOrMa = eligibilityType === 'chc' || eligibilityType === 'medicaid';
      if(!sessionStorage.getItem('querymember') && this.user.isChcOrMa) {
        this.setSecondaryId(response.recipientId);
      }
    })
    );
  }

  setAccessToken(token: string) {
    this.user.accessToken = token;
    if(!!token) {
      sessionStorage.setItem('access-token', token);
    }
  }

  getIdToken() {
    return this.user.idToken;
  }

  getAccessToken() {
    if(!this.user.accessToken) {
      this.user.accessToken = sessionStorage.getItem('access-token');
    }
    return this.user.accessToken;
  }

  setReferralURL() {
    if(document.referrer) {
      let url = new URL(document.referrer);
      if(url.host.includes('adminportal')) {
        this.user.isReferralAdmin = true;
      }
    }
  }

  // ADD memberCardIdValid, Mobile Team won't send token, so to
  // make sure we get logged in we add extra param.
  // we are only passing this param from Login form Component in ngOnInit.
  checkLogin(isPCP: boolean, memberCardIdValid?: string): Observable<boolean> {
    return new Observable(observer => {
      if(!!this.getIdToken() || memberCardIdValid) {
        this.setReferralURL();
        if(this.user.primaryMemberId) {
          this.user.isMember = true;
          this.user.isPCP = isPCP;
          this.isRecipient().subscribe(result => {
            this.user.isChcOrMa = result;
            if(!this.user.isChcOrMa && this.user.secondaryMemberId !== this.user.primaryMemberId) {
              this.user.isDependent = true;
              if(!this.user.secondaryMemberId) {
                if(this.user.primaryMemberId.endsWith('01')) {
                  this.user.isDependent = false;
                } else {
                  this.setSecondaryId(this.user.primaryMemberId);
                }
              }
            }
            observer.next(true);
            observer.complete();
          }, error => {
            observer.next(false);
            observer.complete();
          });
        } else {
          observer.next(false);
          observer.complete();
        }
      } else {
        // this code is ran on browser refreshes
        this.auth.getUserComputed().subscribe(user => {
          if(user) {
            this.setReferralURL();
            this.user.idToken = user.id_token;
            this.user.isMember = true;
            this.user.isPCP = isPCP;
            this.setPrimaryIdFromProfile(user.profile);
            // when this is undefined then this is an ADD token; grab the access token
            if(!this.user.primaryMemberId) {
              var accessToken = this.getAccessToken();
              // TODO update hp/oidc-client and use the decrpyt method
              const payload = KJUR.jws.JWS.parse(accessToken).payloadObj;
              this.setPrimaryIdFromProfile(payload);
            }
            this.user.secondaryMemberId = sessionStorage.getItem('querymember');
            this.isRecipient().subscribe(result => {
              this.user.isChcOrMa = result;
              if(!this.user.isChcOrMa && this.user.secondaryMemberId && this.user.secondaryMemberId !== this.user.primaryMemberId) {
                this.user.isDependent = true;
              }
              observer.next(true);
            }, error => {
              observer.next(false);
            }, () => observer.complete());
          } else {
            observer.next(false);
          }
        }, error => {
          observer.next(false);
          observer.complete();
        });
      }
    });
  }

  private isRecipient(): Observable<boolean> {
    return new Observable<boolean>(observer => {
      this.getConsumerProfile().subscribe((response: { eligibilityType: string }) => {
        const eligibilityType = response.eligibilityType.toLocaleLowerCase();
        const chcOrMa = eligibilityType === 'chc' || eligibilityType === 'medicaid';
        observer.next(chcOrMa);
        observer.complete();
      });
    }
    );
  }

  private getConsumerProfile(): Observable<any> {
    return this.http.get(this.consumerProfileUrl, { headers: { 'useAuth': 'token' } });
  }

  isUserPCP() {
    return this.user.isPCP;
  }

  isUserMember() {
    return this.user.isMember;
  }

  isExpired() {
    return this.auth.isExpired();
  }

  isReferralAdmin() {
    return this.user.isReferralAdmin;
  }
}

class UserType {
  primaryMemberId = null;
  secondaryMemberId = null;
  isPublic = false;
  isPCP = false;
  isMember = false;
  idToken = null;
  accessToken = null;
  isDependent = false;
  isChcOrMa = false;
  isReferralAdmin = false;
  referralUrl: string;
  tpaClientIdentifier = null;
}